import React, { useState, useRef, useEffect, useCallback } from "react";
import { PiHeartbeatBold } from "react-icons/pi";
import { LiaWeightSolid } from "react-icons/lia";
import { FaTemperatureHigh } from "react-icons/fa";
import { FaHeartbeat } from "react-icons/fa";
import { FiFilePlus } from "react-icons/fi";
import BloodPressureForm from "../manual-reading/blood-pressure/BloodPressureForm";
import WeightForm from "../manual-reading/weight/WeightForm";
import TemperatureForm from "../manual-reading/temperature/TemperatureForm";
import PulseOXForm from "../manual-reading/pulse-ox/PulseOXForm";
import GlucoseForm from "../manual-reading/glucose/GlucoseForm";
import styles from "../../../styles/patient-page-modules/addReadingDialog.module.css";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const READING_TYPES = [
  { value: "blood_pressure", label: "Blood Pressure" },
  { value: "weight", label: "Weight" },
  { value: "temperature", label: "Temperature" },
  { value: "blood_glucose", label: "Blood Glucose" },
  { value: "pulse_ox", label: "Pulse OX" },
];

// Reading type icons (navy circular icons)
const READING_ICONS = {
  blood_pressure: PiHeartbeatBold,
  weight: LiaWeightSolid,
  temperature: FaTemperatureHigh,
  pulse_ox: FaHeartbeat,
  blood_glucose: FiFilePlus,
};

// Reading type labels for subtitle
const READING_LABELS = {
  blood_pressure: "Blood Pressure",
  weight: "Weight",
  temperature: "Temperature",
  pulse_ox: "Pulse OX",
  blood_glucose: "Blood Glucose",
};

/**
 * AddReadingDialog - Compact dialog for adding patient readings
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {string} props.initialReadingType - The initial reading type to show (from chart toolbar)
 * @param {React.RefObject} props.triggerRef - Ref to the trigger button for focus management
 */
const AddReadingDialog = ({ isOpen, onClose, initialReadingType = "blood_pressure", triggerRef }) => {
  const [readingType, setReadingType] = useState(initialReadingType);
  const formRef = useRef(null);

  // Update reading type when initial type changes (e.g., chart toolbar selection changed)
  useEffect(() => {
    setReadingType(initialReadingType);
  }, [initialReadingType]);

  // Close the dialog and return focus to the trigger button.
  // Used for every close path: success, Esc, backdrop, and the × button.
  const handleClose = useCallback(() => {
    onClose();
    if (triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [onClose, triggerRef]);

  // Handle successful form submission
  const handleSuccess = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Handle type change in dialog dropdown
  const handleTypeChange = (newType) => {
    setReadingType(newType);
    // Focus will be moved to first input when form renders
  };

  // Handle save button click
  const handleSave = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  // Get the current form's mutation loading state
  // This is passed from each form via a ref
  const [isSaving, setIsSaving] = useState(false);

  // Render the appropriate form based on selected reading type
  const renderForm = () => {
    const Icon = READING_ICONS[readingType];
    const label = READING_LABELS[readingType];

    switch (readingType) {
      case "blood_pressure":
        return (
          <BloodPressureForm
            ref={formRef}
            onSuccess={handleSuccess}
            icon={Icon}
            title={label}
            onLoadingChange={setIsSaving}
          />
        );
      case "weight":
        return (
          <WeightForm
            ref={formRef}
            onSuccess={handleSuccess}
            icon={Icon}
            title={label}
            onLoadingChange={setIsSaving}
          />
        );
      case "temperature":
        return (
          <TemperatureForm
            ref={formRef}
            onSuccess={handleSuccess}
            icon={Icon}
            title={label}
            onLoadingChange={setIsSaving}
          />
        );
      case "pulse_ox":
        return (
          <PulseOXForm
            ref={formRef}
            onSuccess={handleSuccess}
            icon={Icon}
            title={label}
            onLoadingChange={setIsSaving}
          />
        );
      case "blood_glucose":
        return (
          <GlucoseForm
            ref={formRef}
            onSuccess={handleSuccess}
            icon={Icon}
            title={label}
            onLoadingChange={setIsSaving}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} setOpen={handleClose}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader className={styles.dialogHeader}>
          <div className={styles.modalHeaderContent}>
            <DialogTitle className={styles.title}>Add Reading</DialogTitle>
            <Select
              value={readingType}
              onChange={handleTypeChange}
              className={styles.typeDropdown}
            >
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue>
                  {READING_TYPES.find(type => type.value === readingType)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {READING_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogHeader>

        <DialogBody className={styles.dialogBody}>
          {renderForm()}
        </DialogBody>

        <DialogFooter className={styles.dialogFooter}>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={styles.saveButton}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddReadingDialog;
